import {
	SubscriptionStatus,
	SubscriptionTier,
	useCancelScheduledPlanChangeMutation,
	useCancelSubscriptionMutation,
	useChangeSubscriptionPlanMutation,
	useCreateSubscriptionCheckoutSessionMutation,
	useGetMySubscriptionQuery,
	useGetSubscriptionTierConfigsQuery,
	useResumeSubscriptionMutation,
} from '@codezo/graphql';
import { Check, ExternalLink, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { StatusBadge } from '../../components/ui/status-badge';
import { useState } from 'react';
import { EmbeddedCheckoutModal } from './EmbeddedCheckoutModal';
import { PaymentConfirmModal } from './PaymentConfirmModal';

/* ── Helpers ── */

function formatPrice(amount: number, currency?: string | null): string {
	const symbol = (() => {
		switch ((currency || '').toLowerCase()) {
			case 'usd':
				return '$';
			case 'eur':
				return '€';
			case 'gbp':
				return '£';
			case 'dkk':
				return 'kr ';
			default:
				return currency ? `${currency.toUpperCase()} ` : '';
		}
	})();
	return `${symbol}${amount}`;
}

function formatDate(d?: string | null): string {
	if (!d) return '—';
	return new Date(d).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
}

function formatPercent(rate: number): string {
	return `${(rate * 100).toFixed(rate < 0.01 ? 2 : 0)}%`;
}

const TIER_COPY: Record<
	SubscriptionTier,
	{ name: string; description: string; featured: boolean; bullets: string[] }
> = {
	[SubscriptionTier.Creator]: {
		name: 'Creator',
		description: 'For teams starting to create custom products.',
		featured: false,
		bullets: ['Email support'],
	},
	[SubscriptionTier.Pro]: {
		name: 'Pro',
		description: 'For brands producing custom products regularly.',
		featured: true,
		bullets: ['Priority support', 'Account manager'],
	},
	[SubscriptionTier.Enterprise]: {
		name: 'Enterprise',
		description: 'For large organizations with complex production needs.',
		featured: false,
		bullets: ['White-label', 'Dedicated SLA', 'Custom contracts'],
	},
};

const STATUS_TONE: Record<SubscriptionStatus, 'success' | 'info' | 'warn' | 'danger' | 'neutral'> =
	{
		[SubscriptionStatus.Active]: 'success',
		[SubscriptionStatus.Trialing]: 'info',
		[SubscriptionStatus.PastDue]: 'warn',
		[SubscriptionStatus.Unpaid]: 'danger',
		[SubscriptionStatus.Canceled]: 'neutral',
		[SubscriptionStatus.Incomplete]: 'warn',
		[SubscriptionStatus.IncompleteExpired]: 'neutral',
	};

const STATUS_LABEL: Record<SubscriptionStatus, string> = {
	[SubscriptionStatus.Active]: 'Active',
	[SubscriptionStatus.Trialing]: 'Trial',
	[SubscriptionStatus.PastDue]: 'Past due',
	[SubscriptionStatus.Unpaid]: 'Unpaid',
	[SubscriptionStatus.Canceled]: 'Canceled',
	[SubscriptionStatus.Incomplete]: 'Incomplete',
	[SubscriptionStatus.IncompleteExpired]: 'Expired',
};

/* ════════════════════════ SUBSCRIPTION TAB ════════════════════════ */

export function SubscriptionTab() {
	const {
		data: subData,
		loading: subLoading,
		refetch,
	} = useGetMySubscriptionQuery({
		fetchPolicy: 'no-cache',
	});
	const { data: tiersData, loading: tiersLoading } = useGetSubscriptionTierConfigsQuery();

	const [createCheckout, { loading: creatingCheckout }] =
		useCreateSubscriptionCheckoutSessionMutation();
	const [changePlan, { loading: changingPlan }] = useChangeSubscriptionPlanMutation();
	const [cancelScheduledPlanChange, { loading: cancelingScheduled }] =
		useCancelScheduledPlanChangeMutation();
	const [cancelSubscription] = useCancelSubscriptionMutation();
	const [resumeSubscription] = useResumeSubscriptionMutation();

	/* ── Modal state ── */
	const [checkout, setCheckout] = useState<{ open: boolean; clientSecret: string | null }>({
		open: false,
		clientSecret: null,
	});
	const [scaConfirm, setScaConfirm] = useState<{ open: boolean; clientSecret: string | null }>({
		open: false,
		clientSecret: null,
	});
	const [cancelOpen, setCancelOpen] = useState(false);
	const [changePlanConfirm, setChangePlanConfirm] = useState<{
		open: boolean;
		tier: SubscriptionTier;
		kind: 'upgrade' | 'downgrade';
	} | null>(null);

	const subscription = subData?.getMySubscription;
	const tiers = tiersData?.getSubscriptionTierConfigs ?? [];

	const hasActive =
		!!subscription &&
		subscription.status !== SubscriptionStatus.Canceled &&
		subscription.status !== SubscriptionStatus.IncompleteExpired;

	/* ── New subscription (no existing plan) ── */
	const handleSubscribe = async (tier: SubscriptionTier) => {
		if (tier === SubscriptionTier.Enterprise) {
			window.location.href = 'mailto:mail@05creation.com?subject=Enterprise%20plan';
			return;
		}
		try {
			const { data } = await createCheckout({ variables: { tier } });
			const resp = data?.createSubscriptionCheckoutSession;
			if (!resp?.success || !resp.clientSecret) {
				toast.error(resp?.message ?? 'Could not start checkout');
				return;
			}
			setCheckout({ open: true, clientSecret: resp.clientSecret });
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Could not start checkout');
		}
	};

	/* ── Change plan (existing subscription) ── */
	const classifyChange = (targetTier: SubscriptionTier): 'upgrade' | 'downgrade' => {
		if (!subscription) return 'upgrade';
		const current = tiers.find((t) => t.tier === subscription.tier);
		const target = tiers.find((t) => t.tier === targetTier);
		const cp = Number(current?.monthlyPrice ?? 0);
		const tp = Number(target?.monthlyPrice ?? 0);
		return tp >= cp ? 'upgrade' : 'downgrade';
	};

	const handleChangePlanClick = (tier: SubscriptionTier) => {
		if (tier === SubscriptionTier.Enterprise) {
			window.location.href = 'mailto:mail@05creation.com?subject=Enterprise%20upgrade';
			return;
		}
		setChangePlanConfirm({
			open: true,
			tier,
			kind: classifyChange(tier),
		});
	};

	const handleChangePlanConfirm = async () => {
		if (!changePlanConfirm) return;
		const { tier, kind } = changePlanConfirm;

		try {
			const { data } = await changePlan({ variables: { tier } });
			const resp = data?.changeSubscriptionPlan;

			if (!resp?.success) {
				toast.error(resp?.message ?? 'Could not change plan');
				return;
			}

			// SCA required — open payment confirmation modal
			if (resp.requiresAction && resp.clientSecret) {
				setChangePlanConfirm(null);
				setScaConfirm({ open: true, clientSecret: resp.clientSecret });
				return;
			}

			toast.success(kind === 'upgrade' ? 'Upgraded successfully' : 'Downgrade scheduled');
			setChangePlanConfirm(null);
			await refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Could not change plan');
		}
	};

	const handleCancelScheduledChange = async () => {
		const { data } = await cancelScheduledPlanChange();
		const ok = data?.cancelScheduledPlanChange.success;
		if (ok) {
			toast.success('Scheduled change canceled');
			await refetch();
		} else {
			toast.error(data?.cancelScheduledPlanChange.message ?? 'Could not cancel');
		}
	};

	/* ── Cancel / Resume ── */
	const handleCancel = async () => {
		try {
			const { data } = await cancelSubscription();
			if (data?.cancelSubscription.success) {
				toast.success('Cancellation scheduled');
				setCancelOpen(false);
				refetch();
			} else {
				toast.error(data?.cancelSubscription.message ?? 'Failed to cancel');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to cancel');
		}
	};

	const handleResume = async () => {
		try {
			const { data } = await resumeSubscription();
			if (data?.resumeSubscription.success) {
				toast.success('Subscription resumed');
				refetch();
			} else {
				toast.error(data?.resumeSubscription.message ?? 'Failed to resume');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to resume');
		}
	};

	/* ── Checkout complete ── */
	const handleCheckoutComplete = async () => {
		await refetch();
	};

	const handleScaConfirmed = async () => {
		await refetch();
		toast.success('Upgrade complete');
	};

	if (subLoading && !subData) {
		return (
			<div className="flex flex-col gap-4">
				{Array.from({ length: 2 }).map((_, i) => (
					<div
						key={i}
						className="h-[160px] animate-pulse rounded-[var(--r-3)] border border-line-1 bg-surface"
					/>
				))}
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-4">
			{/* ── Current plan ── */}
			{hasActive && subscription ? (
				<Card className="border-line-1 bg-surface px-4 py-4">
					<div className="flex items-start justify-between">
						<div>
							<div className="mb-1.5 text-2xs font-medium uppercase tracking-[0.08em] text-text-4">
								Current plan
							</div>
							<div className="mb-1 flex flex-wrap items-center gap-2">
								<span className="text-xl font-medium text-text-1">
									{TIER_COPY[subscription.tier].name}
								</span>
								<StatusBadge
									active
									activeLabel={STATUS_LABEL[subscription.status]}
									activeTone={STATUS_TONE[subscription.status]}
								/>
								{subscription.cancelAtPeriodEnd && (
									<StatusBadge
										active
										activeLabel={`Ends ${formatDate(subscription.currentPeriodEnd)}`}
										activeTone="warn"
									/>
								)}
								{subscription.pendingTier && !subscription.cancelAtPeriodEnd && (
									<StatusBadge
										active
										activeLabel={`Switching to ${TIER_COPY[subscription.pendingTier].name} on ${formatDate(subscription.pendingTierEffectiveAt)}`}
										activeTone="info"
									/>
								)}
							</div>
							<div className="text-sm text-text-3">
								{subscription.cancelAtPeriodEnd
									? `Access remains until ${formatDate(subscription.currentPeriodEnd)}.`
									: subscription.pendingTier
										? `You'll switch to ${TIER_COPY[subscription.pendingTier].name} on ${formatDate(subscription.pendingTierEffectiveAt)}.`
										: `Renews ${formatDate(subscription.currentPeriodEnd)}.`}
							</div>
						</div>
						<div className="flex shrink-0 gap-2">
							{subscription.pendingTier && !subscription.cancelAtPeriodEnd && (
								<Button
									variant="outline"
									size="sm"
									onClick={handleCancelScheduledChange}
									disabled={cancelingScheduled}
								>
									Keep {TIER_COPY[subscription.tier].name}
								</Button>
							)}
							{subscription.cancelAtPeriodEnd ? (
								<Button variant="outline" size="sm" onClick={handleResume}>
									Resume subscription
								</Button>
							) : (
								<Button
									variant="destructive"
									size="sm"
									onClick={() => setCancelOpen(true)}
								>
									Cancel subscription
								</Button>
							)}
						</div>
					</div>
				</Card>
			) : (
				<Card className="border-line-1 bg-surface px-4 py-4">
					<div className="text-base text-text-3">
						You don’t have an active subscription. Pick a plan below to get started.
					</div>
				</Card>
			)}

			{/* ── Tier cards ── */}
			<div>
				<h3 className="mb-4 text-base font-semibold uppercase tracking-[0.04em] text-text-1">
					{hasActive ? 'Change plan' : 'Choose a plan'}
				</h3>

				{tiersLoading ? (
					<div className="grid grid-cols-1 gap-3 md:grid-cols-3">
						{Array.from({ length: 3 }).map((_, i) => (
							<div
								key={i}
								className="h-[280px] animate-pulse rounded-[var(--r-3)] border border-line-1 bg-surface"
							/>
						))}
					</div>
				) : (
					<div className="grid grid-cols-1 gap-3 md:grid-cols-3">
						{tiers.map((tier) => {
							const copy = TIER_COPY[tier.tier];
							const isCurrent = subscription?.tier === tier.tier && hasActive;
							const isEnterprise = tier.tier === SubscriptionTier.Enterprise;
							const isPending = subscription?.pendingTier === tier.tier;

							return (
								<Card
									key={tier.tier}
									className={`h-full border-line-1 bg-surface-sunken px-4 py-5 ${
										copy.featured ? 'ring-1 ring-accent' : ''
									}`}
								>
									<div className="flex h-full flex-col gap-4">
										{/* Title */}
										<div className="flex items-center gap-2">
											<span className="text-lg font-medium text-text-1">
												{copy.name}
											</span>
											{copy.featured && (
												<span className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-2xs font-medium text-accent">
													<Sparkles size={10} />
													Popular
												</span>
											)}
											{isCurrent && (
												<StatusBadge
													active
													activeLabel="Current"
													activeTone="success"
												/>
											)}
										</div>

										{/* Price */}
										<div>
											{tier.monthlyPrice != null ? (
												<div className="flex items-baseline gap-1">
													<span className="text-2xl font-medium text-text-1">
														{formatPrice(
															tier.monthlyPrice,
															tier.currency
														)}
													</span>
													<span className="text-sm text-text-3">/mo</span>
												</div>
											) : (
												<span className="text-2xl font-medium text-text-1">
													Custom
												</span>
											)}
										</div>

										<p className="text-sm leading-relaxed text-text-3">
											{copy.description}
										</p>

										{/* Bullets */}
										<ul className="flex flex-col gap-2">
											<li className="flex items-center gap-2 text-sm text-text-2">
												<Check
													size={12}
													className="shrink-0 text-status-success"
												/>
												{formatPercent(Number(tier.kickbackRate))} kickback
												on orders
											</li>
											{copy.bullets.map((b) => (
												<li
													key={b}
													className="flex items-center gap-2 text-sm text-text-2"
												>
													<Check
														size={12}
														className="shrink-0 text-status-success"
													/>
													{b}
												</li>
											))}
										</ul>

										{/* Action */}
										<div className="mt-auto [&>button]:w-full">
											{isCurrent ? (
												<Button variant="outline" size="sm" disabled>
													Current plan
												</Button>
											) : isPending ? (
												<Button variant="outline" size="sm" disabled>
													Scheduled
												</Button>
											) : isEnterprise ? (
												<Button
													variant="outline"
													size="sm"
													onClick={() =>
														hasActive
															? handleChangePlanClick(tier.tier)
															: handleSubscribe(tier.tier)
													}
												>
													Contact sales
													<ExternalLink
														size={12}
														data-icon="inline-end"
													/>
												</Button>
											) : hasActive ? (
												<Button
													variant={copy.featured ? 'default' : 'outline'}
													size="sm"
													disabled={changingPlan}
													onClick={() => handleChangePlanClick(tier.tier)}
												>
													{classifyChange(tier.tier) === 'downgrade'
														? `Downgrade to ${copy.name}`
														: `Upgrade to ${copy.name}`}
												</Button>
											) : (
												<Button
													variant={copy.featured ? 'default' : 'outline'}
													size="sm"
													disabled={creatingCheckout}
													onClick={() => handleSubscribe(tier.tier)}
												>
													Choose {copy.name}
												</Button>
											)}
										</div>
									</div>
								</Card>
							);
						})}
					</div>
				)}
			</div>

			{/* ── Stripe embedded checkout (new subscription) ── */}
			<EmbeddedCheckoutModal
				open={checkout.open}
				onOpenChange={(open) => setCheckout((prev) => ({ ...prev, open }))}
				clientSecret={checkout.clientSecret}
				publishableKey={import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? ''}
				title="Start your subscription"
				onComplete={handleCheckoutComplete}
			/>

			{/* ── SCA confirmation (upgrade requiring 3DS) ── */}
			<PaymentConfirmModal
				open={scaConfirm.open}
				onOpenChange={(open) => setScaConfirm((prev) => ({ ...prev, open }))}
				clientSecret={scaConfirm.clientSecret}
				publishableKey={import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? ''}
				onConfirmed={handleScaConfirmed}
			/>

			{/* ── Cancel subscription confirm ── */}
			<ConfirmDialog
				open={cancelOpen}
				onOpenChange={setCancelOpen}
				title="Cancel subscription?"
				description="Your subscription will remain active until the end of the current billing period. You can resume any time before then."
				confirmLabel="Cancel subscription"
				onConfirm={handleCancel}
			/>

			{/* ── Change plan confirm ── */}
			{changePlanConfirm && (
				<ConfirmDialog
					open={changePlanConfirm.open}
					onOpenChange={(open) => {
						if (!open) setChangePlanConfirm(null);
					}}
					title={
						changePlanConfirm.kind === 'upgrade'
							? `Upgrade to ${TIER_COPY[changePlanConfirm.tier].name}?`
							: `Downgrade to ${TIER_COPY[changePlanConfirm.tier].name}?`
					}
					description={
						changePlanConfirm.kind === 'upgrade'
							? "You'll get access right away. We'll charge the prorated difference to your card on file today."
							: "You'll keep your current plan until the end of this billing period, then switch."
					}
					confirmLabel={
						changePlanConfirm.kind === 'upgrade'
							? `Upgrade to ${TIER_COPY[changePlanConfirm.tier].name}`
							: 'Schedule downgrade'
					}
					onConfirm={handleChangePlanConfirm}
					loading={changingPlan}
				/>
			)}
		</div>
	);
}
